import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { tokenStore } from './tokenStore';
import type { ApiErrorBody } from '@/domain/types';

/**
 * The single HTTP client. Every request in the app goes through it, which is
 * what makes token refresh, error normalisation and the 401 handling exist in
 * exactly one place.
 */
export const api: AxiosInstance = axios.create({
  // Same-origin in production behind nginx; Vite proxies it in development, so
  // there is no environment-specific base URL to get wrong.
  baseURL: '/api/v1',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

/** A normalised error every caller can rely on, whatever actually failed. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(message: string, code: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** Field-level messages from a 422, ready to feed into a form. */
  get fieldErrors(): Array<{ field: string; message: string }> {
    if (Array.isArray(this.details)) {
      return this.details as Array<{ field: string; message: string }>;
    }
    return [];
  }
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// --- Refresh handling -------------------------------------------------------

/**
 * Callbacks waiting for an in-flight refresh. Without this queue, a dashboard
 * firing seven parallel requests on a stale token would trigger seven
 * refreshes — and because refresh tokens rotate, six of them would fail and
 * sign the user out.
 */
let refreshPromise: Promise<string> | null = null;

/** Set by the auth store so a failed refresh can tear the session down. */
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) throw new Error('No refresh token');

  // A bare axios call, not `api` — the interceptor would attach the expired
  // access token and recurse.
  const response = await axios.post<{
    data: { accessToken: string; refreshToken: string };
  }>('/api/v1/auth/refresh', { refreshToken });

  const tokens = response.data.data;
  tokenStore.set(tokens.accessToken, tokens.refreshToken);
  return tokens.accessToken;
}

api.interceptors.response.use(
  (response) => response,

  async (error: AxiosError<{ error?: ApiErrorBody }>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    if (!error.response) {
      throw new ApiError(
        error.code === 'ECONNABORTED' ? 'Request timed out' : 'Network error',
        'NETWORK_ERROR',
        0,
      );
    }

    const { status, data } = error.response;
    const body = data?.error;

    // One retry, and never on the auth endpoints themselves.
    const isAuthCall = original?.url?.includes('/auth/');

    if (status === 401 && original && !original._retried && !isAuthCall && tokenStore.getRefresh()) {
      original._retried = true;

      try {
        refreshPromise ??= refreshAccessToken().finally(() => {
          refreshPromise = null;
        });

        const token = await refreshPromise;
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch {
        tokenStore.clear();
        onSessionExpired?.();
        throw new ApiError('Your session has expired', 'SESSION_EXPIRED', 401);
      }
    }

    if (status === 401 && !isAuthCall) {
      tokenStore.clear();
      onSessionExpired?.();
    }

    throw new ApiError(
      body?.message ?? 'Request failed',
      body?.code ?? 'UNKNOWN_ERROR',
      status,
      body?.details,
    );
  },
);

/** Unwraps the `{ data }` envelope so callers work with the payload directly. */
export async function unwrap<T>(promise: Promise<{ data: { data: T } }>): Promise<T> {
  const response = await promise;
  return response.data.data;
}

/** Keeps `meta` alongside the rows, for paginated endpoints. */
export async function unwrapPaged<T>(
  promise: Promise<{ data: { data: T[]; meta: import('@/domain/types').PageMeta } }>,
): Promise<{ rows: T[]; meta: import('@/domain/types').PageMeta }> {
  const response = await promise;
  return { rows: response.data.data, meta: response.data.meta };
}
